import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Observable, Subject, throwError } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { SKIP_LOADER } from '../interceptors/loading.interceptor';
import { ProfessionalPreRegistration, Alert, VRFQuestionnaire, VRFQuestionnaireResponse } from '../models/models';
import {
  BackendApiResponse,
  ProfessionalPaymentRateConfig,
  ProfessionalContractRequest,
  ProfessionalContractPaymentRateAnswer,
  ProfessionalContractPaymentRatesWrapper,
  ProfessionalContractPaymentRatesResponse,
  ProfessionalContractsFilterParams,
  PaginatedData,
  ProfessionalContractListItem,
  ProfessionalDashboardCardData,
  ProfessionalRegistryItem,
  PageableContent,
  ProfessionalsListFilters,
  ProfessionalSummaryResponse,
  NetworkInfo,
  NetworkBodyShop,
  AddNetworkBodyShopRequest,
} from '../models/professional.models';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class ProfessionalsService {
  private readonly http = inject(HttpClient);
  private readonly alertsBaseUrl = `${environment.coreApiUrl}/api/v1/alerts`;

  /** Emette ogni volta che un alert viene preso in carico o risolto */
  readonly alertsChanged$ = new Subject<void>();
  private readonly baseUrl = `${environment.professionalsApiUrl}/api/v1`;
  private readonly contractsBaseUrl = `${environment.backofficeApiUrl}/api/v1/contracts`;

  /**
   * Get authenticated user's professional profile
   * Returns professional profile if exists, or NOT_FOUND error
   */
  getAuthProfile(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/auth/profile`);
  }

  /**
   * Register a new professional (pre-registration)
   */
  userPreRegistration(data: ProfessionalPreRegistration): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/professionals`, data);
  }

  /**
   * Get professionals list with filtering and pagination
   * Endpoint: GET /api/v1/professionals (API v0.0.4)
   * 
   * @param page Page number (0-indexed)
   * @param size Page size
   * @param sort Sort field (e.g., 'id,asc' or 'createdAt,desc')
   * @param filters Filter criteria (type, status, businessName, etc.)
   * @returns Pageable response with ProfessionalSummaryResponse items
   */
  getProfessionals(
    page?: number,
    size?: number,
    sort?: string,
    filters?: ProfessionalsListFilters,
    skipLoader: boolean = false
  ): Observable<BackendApiResponse<PageableContent<ProfessionalSummaryResponse>>> {
    let params = new HttpParams();
    
    // Add pagination parameters
    if (page !== undefined) {
      params = params.set('page', page.toString());
    }
    if (size !== undefined) {
      params = params.set('size', size.toString());
    }
    if (sort) {
      params = params.set('sort', sort);
    }
    
    // Add filter parameters (multi-select arrays use repeated query params)
    if (filters) {
      if (filters.type && filters.type.length > 0) {
        filters.type.forEach(t => {
          params = params.append('type', t);
        });
      }
      if (filters.professionalStatus && filters.professionalStatus.length > 0) {
        filters.professionalStatus.forEach(s => {
          params = params.append('professionalStatus', s);
        });
      }
      if (filters.legalOrPhysical && filters.legalOrPhysical.length > 0) {
        filters.legalOrPhysical.forEach(lop => {
          params = params.append('legalOrPhysical', lop);
        });
      }
      if (filters.companyType) {
        params = params.set('companyType', filters.companyType);
      }
      if (filters.businessName) {
        params = params.set('businessName', filters.businessName);
      }
      if (filters.id) {
        params = params.set('id', filters.id);
      }
      if (filters.identificationCode) {
        params = params.set('identificationCode', filters.identificationCode);
      }
      if (filters.createdAtFrom) {
        params = params.set('createdAtFrom', filters.createdAtFrom);
      }
      if (filters.createdAtTo) {
        params = params.set('createdAtTo', filters.createdAtTo);
      }
      if (filters.step) {
        if (Array.isArray(filters.step)) {
          filters.step.forEach(s => { params = params.append('step', s); });
        } else {
          params = params.set('step', filters.step);
        }
      }
      if (filters.stepStatus) {
        if (Array.isArray(filters.stepStatus)) {
          filters.stepStatus.forEach(s => { params = params.append('stepStatus', s); });
        } else {
          params = params.set('stepStatus', filters.stepStatus);
        }
      }
      if (filters.networkId) {
        params = params.set('networkId', filters.networkId);
      }
    }
    
    return this.http.get<BackendApiResponse<PageableContent<ProfessionalSummaryResponse>>>(
      `${this.baseUrl}/professionals`,
      { params, context: skipLoader ? new HttpContext().set(SKIP_LOADER, true) : undefined }
    );
  }

  /**
   * Get professional details by id
   * Endpoint: GET /api/v1/professionals/{id}
   * 
   * ⚠️ DEPRECATED: This endpoint doesn't exist in the API
   * Use getProfessionalGeneralData() instead
   */
  getProfessionalById(
    professionalId: string,
  ): Observable<BackendApiResponse<ProfessionalRegistryItem>> {
    console.warn('⚠️ getProfessionalById() called - This endpoint does NOT exist!', {
      professionalId,
      endpoint: `${this.baseUrl}/professionals/${professionalId}`,
      stack: new Error().stack
    });
    return this.http.get<BackendApiResponse<ProfessionalRegistryItem>>(
      `${this.baseUrl}/professionals/${professionalId}`,
    );
  }

  /**
   * Get professional general data
   */
  getProfessionalGeneralData(professionalId: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/professionals/${professionalId}/general-data`);
  }

  /**
   * Update professional general data
   */
  updateProfessionalGeneralData(professionalId: string, data: any): Observable<any> {
    return this.http.patch<any>(
      `${this.baseUrl}/professionals/${professionalId}/general-data`,
      data,
    );
  }

  /**
   * Get professional specific data (type-specific data like auto expert, legal, etc.)
   */
  getProfessionalSpecificData(professionalId: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/professionals/${professionalId}/specific-data`);
  }

  /**
   * Update professional specific data
   */
  updateProfessionalSpecificData(professionalId: string, data: any): Observable<any> {
    return this.http.patch<any>(
      `${this.baseUrl}/professionals/${professionalId}/specific-data`,
      data,
    );
  }

  /**
   * Get auto expert specific data
   */
  getAutoExpertData(autoExpertId: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/auto-experts/${autoExpertId}`);
  }

  /**
   * Update auto expert specific data
   */
  updateAutoExpertData(autoExpertId: string, data: any): Observable<any> {
    return this.http.put<any>(`${this.baseUrl}/auto-experts/${autoExpertId}`, data);
  }

  /**
   * Get RE expert specific data
   */
  getREExpertData(expertId: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/re-experts/${expertId}`);
  }

  /**
   * Update RE expert specific data
   */
  updateREExpertData(expertId: string, data: any): Observable<any> {
    return this.http.put<any>(`${this.baseUrl}/re-experts/${expertId}`, data);
  }

  /**
   * Get investigator specific data
   */
  getInvestigatorData(investigatorId: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/investigators/${investigatorId}`);
  }

  /**
   * Update investigator specific data
   */
  updateInvestigatorData(investigatorId: string, data: any): Observable<any> {
    return this.http.put<any>(`${this.baseUrl}/investigators/${investigatorId}`, data);
  }

  /**
   * Get doctor specific data
   */
  getDoctorData(doctorId: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/doctors/${doctorId}`);
  }

  /**
   * Update doctor specific data
   */
  updateDoctorData(doctorId: string, data: any): Observable<any> {
    return this.http.put<any>(`${this.baseUrl}/doctors/${doctorId}`, data);
  }

  /**   * Get lawyer specific data
   */
  getLawyerData(lawyerId: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/lawyers/${lawyerId}`);
  }

  /**
   * Update lawyer specific data
   */
  updateLawyerData(lawyerId: string, data: any): Observable<any> {
    return this.http.put<any>(`${this.baseUrl}/lawyers/${lawyerId}`, data);
  }

  /**
   * Get bodyshop specific data (for STANDARD, ARS, RCI)
   */
  getBodyshopData(bodyshopId: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/body-shops/${bodyshopId}`);
  }

  /**
   * Update bodyshop specific data (for STANDARD, ARS, RCI)
   */
  updateBodyshopData(bodyshopId: string, data: any): Observable<any> {
    return this.http.put<any>(`${this.baseUrl}/body-shops/${bodyshopId}`, data);
  }

  /**
   * Get hail provider specific data
   */
  getHailProviderData(hailProviderId: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/hail-providers/${hailProviderId}`);
  }

  /**
   * Update hail provider specific data
   */
  updateHailProviderData(hailProviderId: string, data: any): Observable<any> {
    return this.http.put<any>(`${this.baseUrl}/hail-providers/${hailProviderId}`, data);
  }

  // ============================================
  // PROFESSIONAL CONTRACTS / PAYMENT RATES
  // ============================================

  /**
   * Get payment rate configs by professional type.
   * Endpoint: GET /professionals/payment-rates/{professionalType}
   */
  getPaymentRatesByProfessionalType(
    professionalType: string,
  ): Observable<BackendApiResponse<ProfessionalPaymentRateConfig[]>> {
    return this.http.get<BackendApiResponse<ProfessionalPaymentRateConfig[]>>(
      `${this.baseUrl}/professionals/payment-rates/${professionalType}`,
    );
  }

  /**
   * Create a new professional/supplier contract.
   * NOTE: Backend accepts shared contract DTO (supplier + professional sections).
   * Endpoint: POST /contracts
   */
  createProfessionalContract(
    payload: ProfessionalContractRequest,
  ): Observable<BackendApiResponse<any>> {
    return this.http.post<BackendApiResponse<any>>(this.contractsBaseUrl, payload);
  }

  /**
   * Get professional contract payment rates.
   * Endpoint: GET /contracts/{contractId}/payment-rates
   */
  getProfessionalContractPaymentRates(
    contractId: string,
  ): Observable<BackendApiResponse<ProfessionalContractPaymentRatesResponse>> {
    return this.http.get<BackendApiResponse<ProfessionalContractPaymentRatesResponse>>(
      `${this.contractsBaseUrl}/${contractId}/payment-rates`,
    );
  }

  /**
   * Update professional contract payment rates.
   * Endpoint: PUT /contracts/{contractId}/payment-rates
   */
  putProfessionalContractPaymentRates(
    contractId: string,
    payload: ProfessionalContractPaymentRatesWrapper,
  ): Observable<BackendApiResponse<any>> {
    return this.http.put<BackendApiResponse<any>>(
      `${this.contractsBaseUrl}/${contractId}/payment-rates`,
      payload,
    );
  }

  /**
   * Get contracts filtered by professionalId/supplierId/responsibleId/subresponsibleId.
   * NOTE: professionalId and supplierId are mutually exclusive (backend constraint).
   * Endpoint: GET /contracts?professionalId=...&supplierId=...&responsibleId=...&subresponsibleId=...
   */
  getContractsByFilters(
    filters: ProfessionalContractsFilterParams = {},
  ): Observable<BackendApiResponse<PaginatedData<ProfessionalContractListItem>>> {
    if (filters.professionalId && filters.supplierId) {
      return throwError(
        () => new Error('professionalId and supplierId are mutually exclusive query params.'),
      );
    }

    let params = new HttpParams();
    if (filters.professionalId) params = params.set('professionalId', filters.professionalId);
    if (filters.supplierId) params = params.set('supplierId', filters.supplierId);
    if (filters.responsibleId) params = params.set('responsibleId', filters.responsibleId);
    if (filters.subresponsibleId) params = params.set('subresponsibleId', filters.subresponsibleId);

    return this.http.get<BackendApiResponse<PaginatedData<ProfessionalContractListItem>>>(
      this.contractsBaseUrl,
      { params },
    );
  }

  /**
   * Get dashboard card data for professionals.
   * Endpoint: GET /professionals/dashboard-cards
   */
  getDashboardCards(): Observable<BackendApiResponse<ProfessionalDashboardCardData[]>> {
    return this.http.get<BackendApiResponse<ProfessionalDashboardCardData[]>>(
      `${this.baseUrl}/professionals/dashboard-cards`,
    );
  }

  /**
   * Get the contract ID for a professional by status.
   * Endpoint: GET /api/v1/professionals/{professionalId}/contract?status={status}
   * Returns the contractId as a string inside `data`.
   */
  getProfessionalContract(
    professionalId: string,
    status: 'DRAFT' | 'ACTIVE',
  ): Observable<BackendApiResponse<string>> {
    return this.http.get<BackendApiResponse<string>>(
      `${this.baseUrl}/professionals/${professionalId}/contract`,
      { params: { status } },
    );
  }

  /**
   * Update professional registration step (completa lo step corrente)
   * Endpoint: PUT /api/v1/professionals/{id}/registration-step
   * Usato per avanzare il workflow del contratto (es. CONTRACT_SENT → CONTRACT_SIGNATURE)
   */
  updateRegistrationStep(professionalId: string): Observable<any> {
    return this.http.put<any>(
      `${this.baseUrl}/professionals/${professionalId}/registration-step`,
      { status: 'COMPLETED' }
    );
  }

  /**   * Get associated users (collaborators) for a professional
   */
  getAssociatedUsers(professionalId: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/professionals/${professionalId}/associated-users`);
  }

  /**
   * Save associated users (collaborators) for a professional
   * Sends the complete list of collaborators
   */
  saveAssociatedUsers(professionalId: string, associatedUsers: any[]): Observable<any> {
    return this.http.put<any>(
      `${this.baseUrl}/professionals/${professionalId}/associated-users`,
      associatedUsers,
    );
  }

  /**
   * Get collaborators (source_type = 'PROFESSIONAL') for a professional
   * Endpoint: GET /professionals/{id}/collaborators
   */
  getCollaborators(professionalId: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/professionals/${professionalId}/collaborators`);
  }

  /**
   * Save (full-replace) collaborators for a professional
   * Endpoint: PUT /professionals/{id}/collaborators
   */
  saveCollaborators(professionalId: string, collaborators: any[]): Observable<any> {
    return this.http.put<any>(
      `${this.baseUrl}/professionals/${professionalId}/collaborators`,
      collaborators,
    );
  }

  /**
   * Update the completion status of the unified "Administrative Employees and
   * Collaborators" section.
   * Endpoint: PATCH /professionals/{id}/staff-section
   */
  setStaffSectionStatus(professionalId: string, status: 'DRAFT' | 'FILLED'): Observable<any> {
    return this.http.patch<any>(
      `${this.baseUrl}/professionals/${professionalId}/staff-section`,
      { status },
    );
  }

  // ============================================
  // VRF QUESTIONNAIRE
  // ============================================

  /**
   * Save VRF questionnaire for a professional
   * Endpoint: PATCH /professionals/{professionalId}/vrfquestionnaire
   */
  saveVRFQuestionnaire(
    professionalId: string,
    data: VRFQuestionnaire,
  ): Observable<VRFQuestionnaireResponse> {
    return this.http.patch<VRFQuestionnaireResponse>(
      `${this.baseUrl}/professionals/${professionalId}/vrfquestionnaire`,
      data,
    );
  }

  /**
   * Get VRF questionnaire for a professional
   * Endpoint: GET /professionals/{professionalId}/vrfquestionnaire
   */
  getVRFQuestionnaire(professionalId: string): Observable<VRFQuestionnaireResponse> {
    return this.http.get<VRFQuestionnaireResponse>(
      `${this.baseUrl}/professionals/${professionalId}/vrfquestionnaire`,
    );
  }

  // ============================================
  // ALERTS (Tag: alerts)
  // ============================================

  /**
   * Get list of alerts (active or all based on includeDisabled param)
   * @param includeDisabled Optional: include disabled alerts (default: false)
   */
  getAlerts(includeDisabled: boolean = false, skipLoader: boolean = false): Observable<Alert[]> {
    const url = includeDisabled ? `${this.alertsBaseUrl}?includeDisabled=true` : this.alertsBaseUrl;
    const context = skipLoader ? new HttpContext().set(SKIP_LOADER, true) : undefined;
    return this.http.get<any>(url, { context }).pipe(map((res) => this.unwrapArray<Alert>(res)));
  }

  getAlertsByQuestionnaire(professionalId: string, questionnaireId: string): Observable<Alert[]> {
    const url = `${this.alertsBaseUrl}?professionalId=${encodeURIComponent(professionalId)}&questionnaireId=${encodeURIComponent(questionnaireId)}`;
    return this.http.get<any>(url).pipe(map((res) => this.unwrapArray<Alert>(res)));
  }

  /**
   * Take in charge an alert (disabled=true, resolved=false)
   * @param alertId UUID of the alert
   */
  disableAlert(alertId: string): Observable<void> {
    return this.http.patch<any>(`${this.alertsBaseUrl}/${alertId}`, {}).pipe(map(() => undefined));
  }

  /** Alias semantico per "Prendi in carico" */
  takeInChargeAlert(alertId: string): Observable<void> {
    return this.disableAlert(alertId).pipe(tap(() => this.alertsChanged$.next()));
  }

  /**
   * Resolve an alert (resolved=true)
   * @param alertId UUID of the alert
   */
  resolveAlert(alertId: string): Observable<void> {
    return this.http.patch<any>(`${this.alertsBaseUrl}/${alertId}/resolve`, {}).pipe(
      map(() => undefined),
      tap(() => this.alertsChanged$.next()),
    );
  }

  /**
   * Delete an existing alert
   * @param alertId UUID of the alert to delete
   */
  deleteAlert(alertId: string): Observable<void> {
    return this.http.delete<void>(`${this.alertsBaseUrl}/${alertId}`);
  }

  /**
   * Unwraps { status, message, data: T[] } → T[], or returns the response as-is if already an array
   */
  private unwrapArray<T>(res: any): T[] {
    if (Array.isArray(res)) return res as T[];
    if (res && Array.isArray(res.data)) return res.data as T[];
    return [];
  }

  // ==========================================================================
  // NETWORK APIS
  // ==========================================================================

  private get networksUrl(): string {
    return `${this.baseUrl}/networks`;
  }

  getNetwork(professionalId: string): Observable<BackendApiResponse<NetworkInfo>> {
    return this.http.get<BackendApiResponse<NetworkInfo>>(`${this.networksUrl}/${professionalId}`);
  }

  getNetworkBodyShops(networkId: string): Observable<BackendApiResponse<NetworkBodyShop[]>> {
    return this.http.get<BackendApiResponse<NetworkBodyShop[]>>(`${this.networksUrl}/${networkId}/body-shops`);
  }

  getNetworkBodyShopById(bodyShopId: string): Observable<BackendApiResponse<NetworkBodyShop>> {
    return this.http.get<BackendApiResponse<NetworkBodyShop>>(`${this.networksUrl}/body-shops/${bodyShopId}`);
  }

  getNetworkControllante(networkId: string): Observable<BackendApiResponse<NetworkBodyShop>> {
    return this.http.get<BackendApiResponse<NetworkBodyShop>>(`${this.networksUrl}/${networkId}/controllante`);
  }

  addBodyShopToNetwork(networkId: string, request: AddNetworkBodyShopRequest): Observable<BackendApiResponse<NetworkBodyShop>> {
    return this.http.post<BackendApiResponse<NetworkBodyShop>>(`${this.networksUrl}/${networkId}/body-shops`, request);
  }

  removeBodyShopFromNetwork(bodyShopId: string): Observable<BackendApiResponse<null>> {
    return this.http.delete<BackendApiResponse<null>>(`${this.networksUrl}/body-shops/${bodyShopId}`);
  }

  updateBodyShopStatus(bodyShopId: string, action: 'SUSPEND' | 'REACTIVATE'): Observable<BackendApiResponse<NetworkBodyShop>> {
    return this.http.patch<BackendApiResponse<NetworkBodyShop>>(
      `${this.networksUrl}/body-shops/${bodyShopId}/status`,
      null,
      { params: { action } }
    );
  }

  // ── OPERATIONAL LOCATIONS ─────────────────────────────────────────────────────

  getOperationalLocations(parentBodyShopId: string): Observable<BackendApiResponse<any[]>> {
    return this.http.get<BackendApiResponse<any[]>>(
      `${this.baseUrl}/body-shops/${parentBodyShopId}/operational-locations`
    );
  }

  createOperationalLocation(parentBodyShopId: string, request: { registrationEmail: string; legalOrPhysical?: string }): Observable<BackendApiResponse<any>> {
    return this.http.post<BackendApiResponse<any>>(
      `${this.baseUrl}/body-shops/${parentBodyShopId}/operational-locations`,
      { ...request, legalOrPhysical: request.legalOrPhysical ?? 'LEGAL' }
    );
  }
}
